import _orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import type { PopupProps } from 'semantic-ui-react';

import type { NavigableRouteMetadata } from '@routes/utils/nav';
import { useAppSelector } from '@store/hooks';
import { useEntitlements } from '@components/entitlements/entitlements';
import { useFeatureFlags } from '@components/feature-flag/feature-flag';
import { useCheckRoutePermission } from '@components/permissions';
import type { PolicyBundle } from '@features/policy/policies.models';
import { remToPx } from '@utils/dom-utils';

import type { SideNavPopupDataById } from './side-nav-context';
import { useSideNavContext } from './side-nav-context';
import type { SideNavItemOverrides, SideNavItemProps } from './side-nav-item';
import {
  PolicyPopupContent,
  ReturnLink,
  SideNavPopupContent,
} from './side-nav-popup';

/**
 * Simple check to see if the report service is available and active.
 */
export const useHasReportService = (): boolean => {
  const { enabled = false, active = false } = useAppSelector(
    state => state.app.healthCheck.isReportsEnabledAndActive || {},
  );

  return enabled && active;
};

/**
 * Transforms route metadata into a list of props objects to render side nav
 * menu items. The returned list is filtered or modified based on active feature
 * flags, entitlements, and resolved route permissions for the current user.
 */
export const useSideNavItems = (
  data: NavigableRouteMetadata[],
  config: Record<string, SideNavItemOverrides> = {},
): SideNavItemProps[] => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { hasFeatureEntitlement } = useEntitlements();

  const hasRoutePermission = useCheckRoutePermission();

  return useMemo(
    () =>
      data.map<SideNavItemProps>(route => {
        const {
          navbar: { name: id, title: label, icon, category },
          path,
          featureFlag,
          entitlement,
        } = route;

        // Allow route-specific overrides to be applied
        const overrides = config[id];

        return {
          id,
          label,
          icon,
          path,
          category,
          isDisabled: !hasRoutePermission(route) || overrides?.isDisabled,
          isHidden:
            !hasFeatureFlag(featureFlag) ||
            !hasFeatureEntitlement(entitlement) ||
            overrides?.isHidden,
          suffix: overrides?.suffix,
        };
      }),
    [data, config, hasRoutePermission, hasFeatureEntitlement, hasFeatureFlag],
  );
};

/**
 * Creates and returns stateful values for the data that drives additional popup
 * content of individual items in the nav menu. Captures all the setup logic of
 * subscribing to the store and preparing the data to be rendered.
 */
export const useSideNavPopupData = (): SideNavPopupDataById => {
  const routeInfo = useAppSelector(state => state.image.selection.routeInfo);

  const policyData: PolicyBundle[] = useAppSelector(
    state => state.policy?.manager?.data,
  );

  /**
   * Derives the last visited URL within Image Analysis, if available, and
   * holds it in memory until it changes.
   */
  const artifacts = useMemo(() => {
    const routeInfoArr = [
      routeInfo.registry,
      routeInfo.repository,
      routeInfo.tag,
      routeInfo.id ? `${routeInfo.id.substring(0, 13)}...` : undefined,
    ];

    const friendlyPath =
      routeInfoArr.reduce((prev, curr) => {
        if (curr && !prev) {
          prev = curr;
        } else if (curr) {
          prev = `${prev}/${curr}`;
        }
        return prev;
      }, '') || null;

    if (!friendlyPath) return null;

    return {
      friendlyPath,
      path: `${routeInfo.path}`,
    };
  }, [routeInfo]);

  /**
   * If policy data is available, creates a list containing the currently active
   * policy, followed by up to 3 recently updated policies.
   */
  const policy = useMemo(() => {
    if (!Array.isArray(policyData) || !policyData.length) return null;

    // Active policy on top, then up to 3 recently updated
    return _orderBy(
      policyData,
      ['active', 'last_updated'],
      ['desc', 'desc'],
    ).slice(0, 4);
  }, [policyData]);

  return {
    artifacts,
    policy,
  };
};

/**
 * Returns Popup props for a given side nav menu item. Uses values from context
 * to return custom content and behavioral options, if configured, otherwise
 * returns defaults.
 *
 * @param id Unique identifier for the menu item
 * @param label Label text to show in the popup when the sidebar is collapsed
 * @param isActive Whether the current item is the active nav item
 */
export const useSideNavPopup = (
  id: string,
  label: string,
  isActive: boolean,
): PopupProps => {
  const { isExpanded, popupData } = useSideNavContext();

  switch (id) {
    case 'artifacts':
      return {
        content: (
          <SideNavPopupContent label={label}>
            {!isActive && popupData.artifacts && (
              <ReturnLink
                path={popupData.artifacts.path}
                label={popupData.artifacts.friendlyPath}
              />
            )}
          </SideNavPopupContent>
        ),
        disabled: isExpanded && !popupData.artifacts,
        offset: isExpanded ? [0, remToPx(-11.5)] : undefined,
      };

    case 'policy':
      return {
        content: (
          <SideNavPopupContent label={label}>
            {!isActive && popupData.policy && (
              <PolicyPopupContent data={popupData.policy} />
            )}
          </SideNavPopupContent>
        ),
        disabled: isExpanded && !popupData.policy,
        offset: isExpanded ? [0, remToPx(-11.5)] : undefined,
      };

    default:
      return {
        content: <SideNavPopupContent label={label} />,
        disabled: isExpanded,
      };
  }
};
